// L'équipe gratuite (D-153, D-159, D-166) : les pages sans compte.
//  - « Créer mon équipe » (/#/equipe/<jeton>, maquette g1) : le lien reçu par le coach après « Recevoir mon lien » sur la
//    page de vente ; au tutoiement (c'est déjà l'app) ; l'équipe choisie parmi celles lues à la FFBB depuis l'appareil ;
//    le club informé et les données des joueurs dits avant le bouton ; puis le lien de rôle réservé à son adresse (/i/…).
//  - « Votre club sur Arceau » (/#/votre-club/<jeton>[/refuser]) : le lien envoyé à l'adresse officielle ; au vouvoiement ;
//    les équipes, leurs coachs, depuis quand, le nombre de joueurs (rien de personnel), « Valider » et « Refuser ».
//  - Valider (/#/creer/<jeton>, quand le jeton n'est pas celui d'une demande D-088) : inscrire le club comme D-088 ; les
//    équipes déjà là reprises, les autres équipes engagées proposées ; le nom, l'e-mail et le rôle au bureau ; puis le lien
//    du bureau réservé à cette adresse (/i/…, code de connexion habituel).
import './gratuite.css';
import { clubEngagements } from '../lib/ffbb';
import {
  clubRequestValidate, clubRequestView, freeClubRefuse, freeClubValidateLink, freeClubView, readClubOrg, teamSignupCreate, teamSignupView,
  type ClubRequestView, type FreeClubView, type TeamSignupView,
} from '../lib/gratuite-api';
import type { DirectoryRow } from '../domain/annuaire';
import { groupEngagements, seasonFor, titleCase, type TeamDraft } from '../domain/console';
import { cap, dayMonth, draftLine, isEmail, leClub, longDay, markTaken, shortName, youthCategory } from '../domain/gratuite';
import { monogram } from '../domain/brand';
import { playerNoun } from '../domain/labels';
import { $, $$, armed, html, mount, type Html } from './dom';
import { applyBrand } from './theme';

const SITE = 'https://arceau.app/';
const ball = html`<span class="ball" aria-hidden="true"></span>`;
const crest = (name: string, logo: string | null, short: string | null = null): Html => (logo && /^(data:image\/(png|webp|jpeg);|https:\/\/)/.test(logo)
  ? html`<img class="pub-logo" src="${logo}" alt="">`
  : html`<span class="pub-logo pub-mono" aria-hidden="true">${monogram(name, short)}</span>`);
const foot = (line: string) => html`<footer class="pub-foot">${ball}<div><strong>Arceau</strong><small>${line}</small></div></footer>`;
const brand = (id: string, name: string, logo: string | null, color: string | null = null) =>
  applyBrand({ id, name, short_name: null, logo_url: logo, primary_color: color }, false);
const players = (g: string | null | undefined, n = 2) => playerNoun(g === 'F' || g === 'M' ? g : 'X', n);

function missing(root: HTMLElement, o: { title?: string; text: string; vous?: boolean }): void {
  applyBrand(null, false);
  document.title = 'Lien expiré · Arceau';
  mount(root, html`<main class="pub-missing">${ball}
    <h1>${o.title ?? 'Lien expiré ou déjà utilisé'}</h1>
    <p>${o.text}</p>
    <div class="btn-row"><a class="btn btn-primary" href="/">${o.vous ? 'Se connecter' : 'Me connecter'}</a><a class="btn btn-ghost" href="${SITE}#coach">arceau.app</a></div>
  </main>`);
}

// =====================================================================================================================
// « Créer mon équipe » (g1)
// =====================================================================================================================
export async function renderTeamCreate(root: HTMLElement, token: string): Promise<void> {
  let v: TeamSignupView | null = null, offline = false;
  try { v = await teamSignupView(token); } catch { offline = true; }
  if (!v) {
    missing(root, offline ? { title: 'Pas de connexion', text: 'Vérifie ta connexion, puis rouvre le lien reçu par e-mail.' }
      : { text: 'Ce lien sert une fois et reste valable 14 jours. Ton équipe est déjà créée ? Connecte-toi avec l’adresse de ta demande. Sinon, refais une demande sur arceau.app, onglet Coach.' });
    return;
  }
  const view = v;
  const club0 = view.club_name ?? `Club ${view.ffbb_code}`;
  if (view.state === 'full' || view.state === 'refused') {
    brand(`equipe:${token.slice(0, 8)}`, club0, view.logo);
    mount(root, html`<div class="pub sc-pub">
      <header class="pub-head"><div class="pub-id">${crest(club0, view.logo)}<div><h1>${club0}</h1><p>Créer mon équipe · Arceau</p></div></div></header>
      <main class="pub-main"><section class="pub-thanks sc-taken"><p class="pub-kicker">${view.state === 'full' ? 'Déjà sur Arceau' : 'Pas sur Arceau'}</p>
        <h3>${view.state === 'full' ? 'Ce club a déjà son espace' : 'Le club n’a pas validé d’équipe'}</h3>
        <p>${view.state === 'full' ? 'Ce club est déjà sur Arceau : demande une invitation à son bureau.' : 'Le club a refusé qu’une de ses équipes soit sur Arceau : rien n’est créé.'}</p>
        <div class="btn-row"><a class="btn btn-ghost" href="/">J’ai déjà un compte</a></div></section></main>
      ${foot('L’app des clubs de basket')}
    </div>`);
    return;
  }
  const first = view.contact_name.trim().split(/\s+/)[0] ?? '';
  const second = view.state === 'free';
  const season = seasonFor(new Date()).name;
  const freeTeam = view.teams.find(t => t.free) ?? null;
  const d = {
    ffbb: 'searching' as 'searching' | 'found' | 'unknown' | 'down', org: null as DirectoryRow | null,
    teams: [] as (TeamDraft & { taken: string | null })[], pick: -1, manual: { name: '', gender: 'F' as 'F' | 'M' | 'X' },
    busy: false, error: '',
  };
  const clubName = () => (d.org?.name ? titleCase(d.org.name) : club0);
  const picked = () => (d.pick >= 0 ? d.teams[d.pick] ?? null : null);

  const option = (t: TeamDraft & { taken: string | null }, i: number): Html => html`
    <button type="button" class="eg-opt" role="radio" data-i="${i}" aria-checked="${String(i === d.pick)}" ${t.taken ? 'disabled' : ''}>
      <span class="eg-radio" aria-hidden="true"></span>
      <span><strong>${t.name}</strong><small>${draftLine(t)}</small></span>
      ${t.taken ? html`<span class="chip chip-mute">${t.taken}</span>` : ''}
    </button>`;

  function teamSection(): Html {
    if (d.ffbb === 'searching') return html`<section class="pub-sec"><h2>Ton équipe</h2><p class="muted">Lecture des équipes engagées à la FFBB…</p></section>`;
    if (d.ffbb === 'found' && d.teams.length) return html`<section class="pub-sec"><h2>Ton équipe</h2>
      <p class="muted small">Les équipes engagées en ${season}, lues à la FFBB. ${second ? 'Choisis la tienne : elle attendra l’accord du club.' : 'Une seule : ce sera l’équipe gratuite du club.'}</p>
      <div class="eg-opts" role="radiogroup" aria-label="Ton équipe">${d.teams.map(option)}</div></section>`;
    return html`<section class="pub-sec"><h2>Ton équipe</h2>
      <p class="muted small">${d.ffbb === 'down' ? 'La FFBB ne répond pas pour l’instant.' : 'Aucune équipe engagée lue à la FFBB pour ce club.'} Saisis ton équipe : elle se reliera à la FFBB plus tard.</p>
      ${d.ffbb === 'down' ? html`<div class="btn-row"><button type="button" class="btn btn-ghost btn-sm" id="eg-retry">Réessayer la FFBB</button></div>` : ''}
      <div class="eg-manual">
        <label class="field"><span>Nom de l’équipe</span><input id="eg-name" maxlength="80" placeholder="ex. U13 filles" value="${d.manual.name}"></label>
        <label class="field"><span>Équipe</span><select id="eg-gender">
          <option value="F" ${d.manual.gender === 'F' ? 'selected' : ''}>Féminine</option><option value="M" ${d.manual.gender === 'M' ? 'selected' : ''}>Masculine</option>
          <option value="X" ${d.manual.gender === 'X' ? 'selected' : ''}>Mixte</option></select></label>
      </div></section>`;
  }

  function draw(): void {
    const name = clubName();
    brand(`equipe:${token.slice(0, 8)}`, name, d.org?.logo ?? view.logo);
    const t = picked();
    const g = t?.gender ?? d.manual.gender;
    const teamLabel = t?.name ?? (d.manual.name.trim() || 'ton équipe');
    const kids = youthCategory(t?.category ?? null);
    const town = [d.org?.town ?? view.town, d.org?.committee ?? view.committee].filter(Boolean).join(' · ');
    mount(root, html`<div class="pub sc-pub">
      <header class="pub-head"><div class="pub-id">${crest(name, d.org?.logo ?? view.logo)}<div><h1>${name}</h1><p>Créer mon équipe · ${second ? 'en attente du club' : 'gratuite'}</p></div></div></header>
      <main class="pub-main">
        <p class="lead">Bonjour ${first}. Choisis ton équipe : elle arrive avec son calendrier FFBB. ${second ? 'Tu prépares tes séances et tes plays tout de suite.' : `Ensuite, tu invites tes ${players(g)}.`}</p>
        ${d.ffbb === 'found' ? html`<div class="card cn-found"><p class="eyebrow cn-ok">✓ Trouvé sur la FFBB · ${view.ffbb_code}</p><span class="cn-found-name">${name}</span>
          <span class="muted small">${[town, second ? 'déjà son équipe gratuite' : 'pas encore sur Arceau'].filter(Boolean).join(' · ')}</span></div>` : ''}
        ${teamSection()}
        ${second ? html`<div class="eg-box" data-guide="eg-wait-box"><span class="ti eg-i-hourglass" aria-hidden="true"></span><span><span class="eg-lab">Le club valide ton équipe</span>
          Le club a déjà son équipe gratuite${freeTeam ? html` : les ${freeTeam.name}${freeTeam.coach ? ` de ${shortName(freeTeam.coach)}` : ''}` : ''}. <b>La tienne attend l’accord du club</b> : il reçoit une demande à l’adresse qu’il a publiée à la FFBB, et valide en s’inscrivant. En attendant, tu prépares tout, mais tu n’invites personne.</span></div>`
        : html`<div class="eg-box" data-guide="eg-informed"><span class="ti eg-i-mail" aria-hidden="true"></span><span><span class="eg-lab">Le club est informé</span>
          ${cap(leClub(name))} reçoit un e-mail à l’adresse qu’il a publiée à la FFBB : <b>« ${view.contact_name}, coach des ${teamLabel}, a inscrit son équipe sur Arceau. »</b> Il n’a rien à faire, rien à payer.</span></div>`}
        <div class="eg-box"><span class="ti eg-i-shield" aria-hidden="true"></span><span><span class="eg-lab">Les données de tes ${players(g)}</span>
          Elles restent celles du club, <b>responsable</b> ; Arceau les héberge.${kids ? html` Les ${t?.category ?? 'jeunes'} sont ${g === 'F' ? 'mineures' : 'mineurs'} : <b>ce sont leurs parents qui acceptent</b>, par le lien que tu leur envoies.` : ''} Jamais de publicité.</span></div>
        <p class="form-msg" role="alert">${d.error}</p>
        <div class="eg-cta"><button type="button" class="btn btn-primary" id="eg-create" ${d.busy ? 'disabled' : ''}>${d.busy ? 'Création de ton équipe…' : 'Créer mon équipe'}</button>
          <p class="hint">${second ? 'Rien à payer. Le club valide, ou pas : ce que tu prépares reste.' : `Gratuit toute la saison ${season}, sans carte bancaire.`}</p></div>
      </main>
      ${foot(`L’app des clubs de basket · lien personnel, valable jusqu’au ${dayMonth(view.expires_at)}`)}
    </div>`);
    for (const b of $$<HTMLButtonElement>(root, '.eg-opt[data-i]')) b.addEventListener('click', () => { d.pick = Number(b.dataset.i); d.error = ''; draw(); });
    $(root, '#eg-retry')?.addEventListener('click', () => void lookup());
    $<HTMLInputElement>(root, '#eg-name')?.addEventListener('input', e => { d.manual.name = (e.target as HTMLInputElement).value; });
    $<HTMLSelectElement>(root, '#eg-gender')?.addEventListener('change', e => { d.manual.gender = (e.target as HTMLSelectElement).value as 'F' | 'M' | 'X'; draw(); });
    $(root, '#eg-create')!.addEventListener('click', () => void create());
  }

  async function lookup(): Promise<void> {
    d.ffbb = 'searching'; draw();
    try {
      const [org, engs] = await Promise.all([readClubOrg(view.ffbb_code), clubEngagements(view.ffbb_code)]);
      d.org = org;
      d.teams = markTaken(groupEngagements(engs), view.teams);
      d.ffbb = org || d.teams.length ? 'found' : 'unknown';
      const free = d.teams.findIndex(x => !x.taken);
      d.pick = d.teams.filter(x => !x.taken).length === 1 ? free : -1;
    } catch { d.ffbb = 'down'; d.teams = []; }
    draw();
  }

  async function create(): Promise<void> {
    if (d.busy) return;
    const t = picked();
    const manual = !(d.ffbb === 'found' && d.teams.length);
    if (!manual && !t) { d.error = 'Choisis ton équipe dans la liste.'; draw(); return; }
    if (manual && d.manual.name.trim().length < 2) { d.error = 'Le nom de ton équipe, s’il te plaît.'; draw(); return; }
    d.busy = true; d.error = ''; draw();
    try {
      const team = t ? { name: t.name, category: t.category, gender: t.gender, links: t.links.slice(0, 12) }
        : { name: d.manual.name.trim().replace(/\s+/g, ' ').slice(0, 80), category: null, gender: d.manual.gender, links: [] };
      const r = await teamSignupCreate(token, { club: d.org ? { ...d.org, name: titleCase(d.org.name) } : {}, team });
      location.replace(`/i/${r.invite.token}`);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      d.busy = false;
      if (err.code === 'P0002') { missing(root, { text: 'Ce lien sert une fois et reste valable 14 jours. Ton équipe est déjà créée ? Connecte-toi avec l’adresse de ta demande.' }); return; }
      d.error = err.code === '23505' ? (err.message ?? 'Déjà sur Arceau.') : err.code === '22023' ? 'Vérifie le nom de ton équipe.' : 'Création impossible pour l’instant. Réessaie dans un instant.';
      draw();
    }
  }

  draw();
  void lookup();
}

// =====================================================================================================================
// « Votre club sur Arceau » (sans compte, vouvoiement)
// =====================================================================================================================
export async function renderClubView(root: HTMLElement, token: string, refuse: boolean): Promise<void> {
  let v: FreeClubView | null = null, offline = false;
  try { v = await freeClubView(token); } catch { offline = true; }
  if (!v) {
    missing(root, offline ? { title: 'Pas de connexion', text: 'Vérifiez votre connexion, puis rouvrez le lien reçu par e-mail.', vous: true }
      : { title: 'Lien introuvable', text: 'Ce lien ne correspond à aucun club sur Arceau. Vérifiez l’adresse reçue par e-mail.', vous: true });
    return;
  }
  const view = v;
  brand(`club:${token.slice(0, 8)}`, view.club_name, view.logo, view.color);
  document.title = `${view.club_name} · Arceau`;
  const nWait = view.teams.filter(t => !t.free).length;
  const rowOf = (t: FreeClubView['teams'][number]): Html => html`<li>
    <span><strong>${t.name}</strong><small>${[t.coach ? `Coach : ${t.coach}` : 'Coach : à venir', `depuis le ${dayMonth(t.since)}`,
      `${t.players} ${players(t.gender, t.players)} ${t.players > 1 ? (t.gender === 'F' ? 'inscrites' : 'inscrits') : t.gender === 'F' ? 'inscrite' : 'inscrit'}`].join(' · ')}</small></span>
    ${view.status === 'free' ? html`<span class="eg-acts">
      <span class="chip ${t.free ? 'chip-ok' : 'chip-warn'}">${t.free ? 'Équipe gratuite' : 'Attend votre accord'}</span>
      <button type="button" class="btn btn-danger-ghost btn-sm" data-refuse="${t.id}" data-free="${t.free ? '1' : ''}">Refuser</button></span>`
      : html`<span class="chip ${t.free ? 'chip-ok' : 'chip-mute'}">${t.free ? 'Équipe gratuite' : 'Au club'}</span>`}
  </li>`;
  const state = view.status === 'validated'
    ? html`<div class="eg-box"><span class="ti ti-check" aria-hidden="true"></span><span><span class="eg-lab">Inscrit le ${dayMonth(view.validated_at!)}</span>
        Le club est sur Arceau, avec son bureau. Pour la suite, connectez-vous à l’espace du club.</span></div>
      <div class="btn-row"><a class="btn btn-primary" href="/">Se connecter</a></div>`
    : view.status === 'refused'
      ? html`<div class="eg-box"><span class="ti eg-i-lock" aria-hidden="true"></span><span><span class="eg-lab">Refusé le ${dayMonth(view.refused_at!)}</span>
          Le club a refusé l’équipe : l’app est en lecture seule, et les données personnelles seront effacées le ${longDay(view.purge_on!)}. Les coachs et les parents ont été prévenus.</span></div>`
      : html`<section class="pub-sec"><h2>Valider</h2>
          <p class="muted">Valider, c’est inscrire le club sur Arceau, en 2 minutes (réservé au bureau) : 30 jours d’essai gratuit pour toutes les équipes et les outils du bureau.
            Les équipes déjà là rejoignent l’espace du club, avec leurs données et leurs coachs.${view.request?.status === 'open' ? ` La demande en cours expire le ${longDay(view.request.expires_at)}.` : ''}</p>
          <p class="form-msg" role="alert" id="eg-vmsg"></p>
          <div class="eg-cta"><button type="button" class="btn btn-primary" id="eg-validate">Valider et inscrire le club</button>
            <p class="hint">Aucun moyen de paiement. Ensuite, le club s’abonne à la saison selon son nombre de licenciés (tarifs sur arceau.app).</p></div></section>`;
  mount(root, html`<div class="pub sc-pub">
    <header class="pub-head"><div class="pub-id">${crest(view.club_name, view.logo, view.short_name)}<div><h1>${view.club_name}</h1><p>Votre club sur Arceau</p></div></div></header>
    <main class="pub-main">
      <p class="lead">${view.status === 'free'
        ? `${nWait ? `${view.teams.length} équipes de votre club se sont inscrites` : 'Une équipe de votre club s’est inscrite'} sur Arceau, l’app des clubs de basket, par ${nWait ? 'leurs coachs' : 'son coach'}. Le club en est responsable ; Arceau héberge les données.`
        : `Les équipes de votre club sur Arceau.`}</p>
      <section class="pub-sec" id="eg-teams"><h2>Les équipes</h2>
        ${refuse && view.status === 'free' ? html`<p class="hint">Choisissez l’équipe à refuser : refuser une équipe qui attend ferme sa demande ; refuser l’équipe gratuite met l’app en lecture seule 30 jours, puis efface les données personnelles.</p>` : ''}
        <div class="card"><ul class="eg-teams">${view.teams.map(rowOf)}</ul></div>
        <p class="muted small">Rien de personnel ici : les noms des joueurs restent dans l’équipe.</p>
        <p class="form-msg" role="alert" id="eg-rmsg"></p></section>
      ${state}
    </main>
    ${foot('L’app des clubs de basket · lien réservé à l’adresse du club publiée à la FFBB')}
  </div>`);
  if (refuse) $(root, '#eg-teams')?.scrollIntoView({ block: 'start' });
  for (const b of $$<HTMLButtonElement>(root, '[data-refuse]')) b.addEventListener('click', async () => {
    if (!armed(b, b.dataset.free ? 'Toute l’équipe gratuite ?' : 'Refuser ?')) return;
    b.disabled = true;
    try { await freeClubRefuse(token, b.dataset.refuse!); await renderClubView(root, token, false); }
    catch { b.disabled = false; $(root, '#eg-rmsg')!.textContent = 'Impossible pour l’instant. Réessayez dans un instant.'; }
  });
  $(root, '#eg-validate')?.addEventListener('click', async e => {
    const b = e.currentTarget as HTMLButtonElement;
    b.disabled = true;
    try { const t = await freeClubValidateLink(token); history.pushState(null, '', `/#/creer/${t}`); window.scrollTo(0, 0); if (!(await renderValidate(root, t))) throw new Error('lien'); }
    catch { b.disabled = false; $(root, '#eg-vmsg')!.textContent = 'Impossible pour l’instant. Réessayez dans un instant.'; }
  });
}

// =====================================================================================================================
// Valider = inscrire le club (D-088), depuis le lien de l'adresse officielle
// =====================================================================================================================
/** La page de validation, si le jeton est celui d'une demande au club ; false sinon (la page D-088 dit « lien expiré »). */
export async function renderValidate(root: HTMLElement, token: string): Promise<boolean> {
  let v: ClubRequestView | null = null;
  try { v = await clubRequestView(token); } catch { return false; }
  if (!v) return false;
  const view = v;
  const season = seasonFor(new Date()).name;
  const d = {
    teams: [] as TeamDraft[], ffbb: (view.ffbb_code ? 'searching' : 'none') as 'searching' | 'found' | 'down' | 'none',
    name: '', email: '', role: '' as '' | 'president' | 'secretaire' | 'tresorier', busy: false, error: '',
  };
  const ROLES = [['president', 'Présidence'], ['secretaire', 'Secrétariat'], ['tresorier', 'Trésorerie']] as const;
  brand(`valider:${token.slice(0, 8)}`, view.club_name, view.logo, view.color);
  document.title = `Inscrire ${view.club_name} · Arceau`;

  function draw(): void {
    const keep = () => {
      d.name = $<HTMLInputElement>(root, '#eg-vname')?.value ?? d.name;
      d.email = $<HTMLInputElement>(root, '#eg-vmail')?.value ?? d.email;
    };
    mount(root, html`<div class="pub sc-pub">
      <header class="pub-head"><div class="pub-id">${crest(view.club_name, view.logo, view.short_name)}<div><h1>${view.club_name}</h1><p>Inscrire le club · essai gratuit</p></div></div></header>
      <main class="pub-main">
        <p class="lead">Valider, c’est inscrire le club sur Arceau : vérifiez le club et ses équipes, puis entrez dans l’espace du club, avec les droits du bureau.</p>
        ${view.ffbb_code ? html`<div class="card cn-found"><p class="eyebrow cn-ok">✓ Club FFBB · ${view.ffbb_code}</p><span class="cn-found-name">${view.club_name}</span>
          <span class="muted small">${[view.town, view.committee, `30 jours d’essai, jusqu’au ${dayMonth(new Date(Date.now() + 30 * 86400000).toISOString())}`].filter(Boolean).join(' · ')}</span></div>` : ''}
        <section class="pub-sec"><h2>Déjà sur Arceau</h2>
          <p class="muted small">Elles rejoignent l’espace du club avec leurs données et leurs coachs : rien n’est à refaire.</p>
          <div class="card"><ul class="eg-teams">${view.teams.map(t => html`<li><span><strong>${t.name}</strong>
            <small>${[t.coach ? `Coach : ${t.coach}` : '', `depuis le ${dayMonth(t.since)}`, `${t.players} ${players(t.gender, t.players)}`].filter(Boolean).join(' · ')}</small></span>
            <span class="chip ${t.free ? 'chip-ok' : 'chip-warn'}">${t.free ? 'Gratuite' : 'Attend'}</span></li>`)}</ul></div></section>
        ${d.ffbb === 'searching' ? html`<p class="muted">Lecture des autres équipes engagées à la FFBB…</p>`
          : d.teams.length ? html`<section class="pub-sec"><h2>Les autres équipes</h2>
            <p class="muted small">Engagées en ${season}, déjà reliées à leurs calendriers FFBB. Décochez celles que vous ne voulez pas.</p>
            <div class="card sc-teams"><ul class="iv-list">${d.teams.map((t, i) => html`<li class="iv-role cn-team">
              <span class="iv-main"><strong>${t.name}</strong><span class="muted small">${draftLine(t)}</span></span>
              <button type="button" class="iv-switch" role="switch" data-i="${i}" aria-checked="${String(t.on)}" aria-label="Créer ${t.name}"></button></li>`)}</ul></div></section>` : ''}
        <section class="pub-sec"><h2>Votre accès</h2>
          <div class="card sc-me">
            <label class="field"><span>Vos prénom et nom</span><input id="eg-vname" maxlength="80" autocomplete="name" value="${d.name}"></label>
            <label class="field"><span>Votre adresse e-mail</span><input id="eg-vmail" type="email" inputmode="email" autocomplete="email" value="${d.email}"></label>
            <div class="field"><span>Votre rôle au bureau</span><div class="eg-role" role="radiogroup" aria-label="Votre rôle au bureau">${ROLES.map(([k, l]) => html`
              <button type="button" data-role="${k}" aria-pressed="${String(d.role === k)}">${l}</button>`)}</div></div>
            <span class="muted small">Vous recevez les droits du bureau ; l’adresse du club reçoit une copie. Pas de mot de passe : un code arrive à votre adresse.</span>
          </div></section>
        <p class="form-msg" role="alert">${d.error}</p>
        <div class="eg-cta"><button type="button" class="btn btn-primary" id="eg-go" ${d.busy ? 'disabled' : ''}>${d.busy ? 'Inscription du club…' : 'Valider et inscrire le club'}</button>
          <p class="hint">30 jours d’essai, sans engagement ni moyen de paiement. Ensuite, le club s’abonne à la saison.</p></div>
      </main>
      ${foot(`L’app des clubs de basket · lien personnel du club, valable jusqu’au ${dayMonth(view.expires_at)}`)}
    </div>`);
    for (const b of $$<HTMLButtonElement>(root, '[data-role]')) b.addEventListener('click', () => { keep(); d.role = b.dataset.role as typeof d.role; draw(); });
    for (const b of $$<HTMLButtonElement>(root, '.iv-switch[data-i]')) b.addEventListener('click', () => {
      const t = d.teams[Number(b.dataset.i)]!; t.on = !t.on; b.setAttribute('aria-checked', String(t.on));
    });
    $(root, '#eg-go')!.addEventListener('click', () => { keep(); void go(); });
  }

  async function lookup(): Promise<void> {
    if (!view.ffbb_code) return;
    try {
      const engs = await clubEngagements(view.ffbb_code);
      d.teams = groupEngagements(engs).filter(t => !t.links.some(l => view.engagements.includes(l.engagement_id)));
      d.ffbb = 'found';
    } catch { d.ffbb = 'down'; }
    draw();
  }

  async function go(): Promise<void> {
    if (d.busy) return;
    if (d.name.trim().length < 2) { d.error = 'Vos prénom et nom, s’il vous plaît.'; draw(); return; }
    if (!isEmail(d.email)) { d.error = 'Une adresse e-mail valide : c’est elle qui ouvre l’espace du club.'; draw(); return; }
    if (!d.role) { d.error = 'Votre rôle au bureau : présidence, secrétariat ou trésorerie.'; draw(); return; }
    d.busy = true; d.error = ''; draw();
    try {
      const r = await clubRequestValidate(token, { name: d.name.trim(), email: d.email.trim().toLowerCase(), role: d.role,
        teams: d.teams.filter(t => t.on).slice(0, 40).map(t => ({ name: t.name, category: t.category, gender: t.gender, links: t.links.slice(0, 12) })) });
      location.replace(`/i/${r.invite.token}`);
    } catch (e) {
      const err = e as { code?: string };
      d.busy = false;
      if (err.code === 'P0002') { missing(root, { text: 'Ce lien de validation a déjà servi ou a expiré (30 jours).', vous: true }); return; }
      d.error = err.code === '22023' ? 'Vérifiez votre nom, votre adresse et votre rôle.' : 'Inscription impossible pour l’instant. Réessayez dans un instant.';
      draw();
    }
  }

  draw();
  void lookup();
  return true;
}
